import { Close, DescriptionOutlined, OpenInFull } from '@mui/icons-material';
import { IconButton, InputBase, Tooltip } from '@mui/material';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/Button';
import { trackerUrls } from '@/tracker/config/urls';
import { QueueRef, TaskBasic } from '@/tracker/types';
import { SearchSelect } from '@/tracker/ui/SearchSelect';
import { getKey, getQueueLabel } from '@/tracker/utils/optionGetters';

import { ALL_TASKS, BoardColumnWithTasks, BoardStore } from '../../model/BoardStore';

import { getTaskLabel } from './getTaskLabel';

import s from './QuickCreatePanel.module.scss';

type Mode = 'new' | 'existing';

const MODES: { id: Mode; label: string }[] = [
  { id: 'new', label: 'Новая' },
  { id: 'existing', label: 'Существующая' },
];

type Props = {
  store: BoardStore;
  boardId: string;
  column: BoardColumnWithTasks;
  onClose: () => void;
};

/** Быстрое создание из «+» колонки, как в Яндекс Трекере: новая задача или существующая на доску */
const QuickCreatePanel: React.FC<Props> = observer(({ store, boardId, column, onClose }) => {
  const [mode, setMode] = useState<Mode>('new');
  const [summary, setSummary] = useState('');
  const [queue, setQueue] = useState<QueueRef | null>(store.quickCreateQueue);
  const [task, setTask] = useState<TaskBasic | null>(null);
  const statuses = column.statuses.map((status) => status.key);
  const canCreate = !!queue && !!summary.trim() && !store.isSaving;

  const fullFormUrl = trackerUrls.newIssueOnBoard({
    boardId,
    statuses,
    sprintId: store.sprintFilter === ALL_TASKS ? null : store.sprintFilter,
    queueKey: queue?.key ?? null,
    summary: summary.trim(),
  });

  const create = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!queue || !canCreate) return;

    if (await store.createInColumn({ queueKey: queue.key, summary: summary.trim(), statuses })) {
      onClose();
    }
  };

  const add = async () => {
    if (task && (await store.addToColumn({ task, statuses }))) onClose();
  };

  return (
    <div className={s.panel}>
      <div className={s.header}>
        <div className={s.modes} role="group" aria-label="Какую задачу добавить">
          {MODES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={cn(s.modes__mode, mode === id && s.modes__mode_active)}
              aria-pressed={mode === id}
              onClick={() => setMode(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <Tooltip title="Открыть полную форму">
          <IconButton
            component={Link}
            to={fullFormUrl}
            size="small"
            aria-label="Открыть полную форму"
          >
            <OpenInFull fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" aria-label="Закрыть" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </div>

      {mode === 'new' && (
        <form className={s.body} onSubmit={create}>
          <div className={s.summary}>
            <DescriptionOutlined fontSize="small" className={s.summary__icon} />
            <InputBase
              fullWidth
              autoFocus
              placeholder="Название задачи"
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              inputProps={{ 'aria-label': 'Название задачи' }}
              sx={{ fontSize: 16 }}
            />
          </div>
          <div className={s.footer}>
            <SearchSelect
              value={queue}
              onChange={setQueue}
              search={store.searchQueues}
              getId={getKey}
              getLabel={getQueueLabel}
              ariaLabel="Очередь"
              placeholder="Очередь"
              sx={{ flex: 1, maxWidth: 320 }}
            />
            <Button type="submit" disabled={!canCreate}>
              Создать
            </Button>
          </div>
        </form>
      )}

      {mode === 'existing' && (
        <div className={s.body}>
          <SearchSelect
            value={task}
            onChange={setTask}
            search={store.searchTasks}
            getId={getKey}
            getLabel={getTaskLabel}
            ariaLabel="Существующая задача"
            placeholder="Ключ или название задачи"
          />
          <div className={s.footer}>
            <span className={s.hint}>Задача встанет в статус этой колонки</span>
            <Button disabled={!task || store.isSaving} onClick={add}>
              Добавить
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

export { QuickCreatePanel };
